import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ActivityIndicator, Box, Button, DatePicker, Grid, Menu, MenuItem, Text, TextInput } from '../../../../ui';
import { Controller } from 'react-hook-form';
import { CustomerAttachment, Dialog, LayoutBox } from '../../../../components';
import { CalendarIcon, CrossIcon, EditIcon, EllipsisIcon, GreenCheckIcon, RedCrossIcon } from '../../../../components/icons';
import moment from 'moment-timezone';
import { formatMoney, getGMTTimestamp } from '../../../../utils';
import { useAdjustmentState } from '../../context/QuoteAdjustment/AdjustmentContext';
import { useAdjustmentForm } from '../../context/QuoteAdjustment/AdjustmentFormContext';
import { QuoteStat } from './QuoteStat';
import { useQuoteData } from '../../context/QuoteDataContext';
import { useApi, useAppState, useAsync, useNavigate } from '../../../../hooks';
import { ShieldCheck } from '../../../../ui/icons';
import { GenerateWorkbookModal } from './GenerateWorkbookModal';
import { QuotePolicyNumberField } from './QuotePolicyNumberField';

const EditableField = ({ value, handleOnPress, shouldShowPicker = true, component }) => {
  return (
    <Box
      style={{
        justifyContent: 'flex-start',
        alignItems: 'center',
        borderWidth: 1,
        borderColor: '#ccc',
        borderRadius: 8,
        fontSize: 16,
      }}
      flexDirection="row"
      paddingLeft={4}
    >
      <>
        {shouldShowPicker && (
          <Box onPress={() => handleOnPress()}>
            <CalendarIcon />
          </Box>
        )}

        {!component ? <Text maxWidth="100%" size="medium" color="#637381" maxLines={2} bold value={value} /> : component}
      </>
    </Box>
  );
};

const OverViewSection = ({ quote }) => {
  const { adjusting } = useAdjustmentState();
  const { setValue, getValues } = useAdjustmentForm();
  const { data } = useQuoteData();
  const { adjustmentFactor, netPremium, locations } = quote;

  const [{ showPremium, showAdjustmentFactor, canAdjustQuote }] = useAppState();
  const [isStartDateEditable, setIsStartDateEditable] = useState(false);
  const [startEditingStartDate, setStartEditingStartDate] = useState(false);
  const [startDatePickerOpen, setStartDatePickerOpen] = useState(false);
  const [startDate, setStartDate] = useState(quote.coverageAt ? moment.utc(quote.coverageAt) : moment.utc());

  const [isEndDateEditable, setIsEndDateEditable] = useState(false);
  const [startEditingEndDate, setStartEditingEndDate] = useState(false);
  const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);
  const [endDate, setEndDate] = useState(quote.coverageUntil ? moment.utc(quote.coverageUntil) : moment(startDate).add(1, 'year'));

  const [isAdjustmentEditable, setIsAdjustmentEditable] = useState(false);
  const [startEditingAdjustment, setStartEditingAdjustment] = useState(false);
  const [adjustmentFactorValue, setAdjustmentFactorValue] = useState(adjustmentFactor.toFixed(6));
  const [targetPremiumValue, setTargetPremiumValue] = useState(quote.targetPremium || '');

  const [isPremiumEditable, setIsPremiumEditable] = useState(false);
  const [startEditingPremium, setStartEditingPremium] = useState(false);

  const [lossPickValue, setLossPickValue] = useState(quote.lossPick || '');
  const [isLossPickEditable, setIsLossPickEditable] = useState(false);
  const [startEditingLossPick, setStartEditingLossPick] = useState(false);
  const [arrayOfPerOccDedOptionsValue, setArrayOfPerOccDedOptionsValue] = useState(
    quote.arrayOfPerOccDedOptions ? quote.arrayOfPerOccDedOptions.join(',') : ''
  );
  const [isPerOccDedEditable, setIsPerOccDedEditable] = useState(false);
  const [startEditingPerOccDed, setStartEditingPerOccDed] = useState(false);

  const handleSetFieldEditable = (field) => {
    setIsStartDateEditable(field === 'startDate');
    setIsEndDateEditable(field === 'endDate');
    setIsAdjustmentEditable(field === 'adjustmentFactor');
    setIsPremiumEditable(field === 'targetPremium');
    setIsLossPickEditable(field === 'lossPick');
    setIsPerOccDedEditable(field === 'arrayOfPerOccDedOptions');
  };

  useEffect(() => {
    setStartDate(moment.utc(quote.coverageAt));
    setEndDate(moment.utc(quote.coverageUntil));
    setAdjustmentFactorValue(quote.adjustmentFactor.toFixed(6));
  }, [quote, netPremium]);

  useEffect(() => {
    setTargetPremiumValue(quote.targetPremium || '');
  }, [quote.targetPremium]);

  useEffect(() => {
    setLossPickValue(quote.lossPick || '');
  }, [quote.lossPick]);

  useEffect(() => {
    setArrayOfPerOccDedOptionsValue(quote.arrayOfPerOccDedOptions ? quote.arrayOfPerOccDedOptions.join(',') : '');
  }, [quote.arrayOfPerOccDedOptions]);

  useEffect(() => {
    setArrayOfPerOccDedOptionsValue(quote.arrayOfPerOccDedOptions ? quote.arrayOfPerOccDedOptions.join(',') : '');
  }, [quote.arrayOfPerOccDedOptions]);

  return (
    <LayoutBox
      row
      flexDirection="column"
      alignItems="flex-start"
      alignSelf="stretch"
      flex="1 0 0"
      padding="24px"
      borderRadius="8px"
      backgroundColor="#fff"
      width="50%"
    >
      <Text row style={{ fontWeight: 600, fontSize: 16, maxLines: 2 }} color=" #212B36">
        Overview
      </Text>
      <LayoutBox row alignItems="center" padding="8px" paddingLeft="0px" gap={8} alignSelf="stretch">
        <Text style={{ color: '#212B36', fontSize: 14, fontStyle: 'normal', fontWeight: 500, flex: '1 0 0' }}>Start Date </Text>
        <Box style={{ color: '#637381', fontSize: 14, fontStyle: 'normal', fontWeight: 500, flex: '1 0 0' }}>
          {startEditingStartDate && adjusting ? (
            <EditableField
              component={
                <TextInput label="Start Date" value={startDate.format('MM-DD-YY')} variant="filled" placeholder="0" bold alignItems="center" />
              }
              handleOnPress={() => setStartDatePickerOpen(true)}
            />
          ) : (
            <div
              style={{ display: 'flex', alignItems: 'center', cursor: `${adjusting ? 'pointer' : ''}` }}
              onMouseEnter={() => canAdjustQuote && handleSetFieldEditable('startDate')}
            >
              <Text maxWidth="100%" size="medium" color="#637381" maxLines={2} bold ma>
                {startDate.format('MM/DD/YYYY')}
              </Text>
            </div>
          )}
        </Box>
        <Box height="24px" width="24px" alignItems="center" flexDirection="row" marginRight={4}>
          {isStartDateEditable && !startEditingStartDate && adjusting ? (
            <Box
              onPress={() => {
                setStartEditingStartDate(true);
              }}
            >
              <EditIcon />
            </Box>
          ) : (
            startEditingStartDate &&
            adjusting && (
              <>
                <Box
                  onPress={() => {
                    if (adjusting) {
                      const newEndDate = moment(startDate).add(1, 'year');
                      setValue('quote.coverageAt', getGMTTimestamp(moment(Date.parse(startDate)).format('DD-MM-YYYY')));
                      setValue('quote.coverageUntil', getGMTTimestamp(moment(Date.parse(newEndDate)).format('DD-MM-YYYY')));
                      setEndDate(newEndDate);
                    }
                    setStartEditingStartDate(false);
                  }}
                >
                  <GreenCheckIcon />
                </Box>
                <Box
                  onPress={() => {
                    setIsStartDateEditable(false);
                    setStartEditingStartDate(false);
                    setStartDate(moment.utc(quote.coverageAt));
                  }}
                >
                  <CrossIcon />
                </Box>
              </>
            )
          )}
        </Box>
        <Dialog open={startDatePickerOpen} onClose={() => setStartDatePickerOpen(false)}>
          <DatePicker
            date={startDate}
            onChange={(date) => {
              setStartDate(date);
              setStartDatePickerOpen(false);
            }}
          />
        </Dialog>
      </LayoutBox>
      <LayoutBox row alignItems="center" padding="8px" paddingLeft="0px" gap={8} alignSelf="stretch">
        <Text style={{ color: '#212B36', fontSize: 14, fontStyle: 'normal', fontWeight: 500, flex: '1 0 0' }}>End Date </Text>
        <Box style={{ color: '#637381', fontSize: 14, fontStyle: 'normal', fontWeight: 500, flex: '1 0 0' }}>
          {startEditingEndDate && adjusting ? (
            <EditableField
              component={
                <TextInput
                  label="End Date"
                  value={endDate.format('MM-DD-YY')}
                  variant="filled"
                  placeholder="0"
                  type="text"
                  bold
                  alignItems="center"
                />
              }
              handleOnPress={() => setEndDatePickerOpen(true)}
            />
          ) : (
            <div
              style={{ display: 'flex', alignItems: 'center', cursor: `${adjusting ? 'pointer' : ''}` }}
              onMouseEnter={() => {
                canAdjustQuote && handleSetFieldEditable('endDate');
              }}
            >
              <Text maxWidth="100%" size="medium" color="#637381" maxLines={2} bold ma>
                {endDate.format('MM/DD/YYYY')}
              </Text>
            </div>
          )}
        </Box>
        <Box height="24px" width="24px" alignItems="center" flexDirection="row" marginRight={4}>
          {isEndDateEditable && !startEditingEndDate && adjusting ? (
            <Box
              onPress={() => {
                setStartEditingEndDate(true);
              }}
            >
              <EditIcon />
            </Box>
          ) : (
            startEditingEndDate &&
            adjusting && (
              <>
                <Box
                  onPress={() => {
                    adjusting && setValue('quote.coverageUntil', getGMTTimestamp(moment(Date.parse(endDate)).format('DD-MM-YYYY')));
                    setStartEditingEndDate(false);
                  }}
                >
                  <GreenCheckIcon />
                </Box>
                <Box
                  onPress={() => {
                    setIsEndDateEditable(false);
                    setStartEditingEndDate(false);
                    setEndDate(moment.utc(quote.coverageUntil));
                  }}
                >
                  <CrossIcon />
                </Box>
              </>
            )
          )}
        </Box>
        <Dialog open={endDatePickerOpen} onClose={() => setEndDatePickerOpen(false)}>
          <DatePicker
            date={endDate}
            onChange={(date) => {
              setEndDate(date);
              setEndDatePickerOpen(false);
            }}
          />
        </Dialog>
      </LayoutBox>
      <LayoutBox row alignItems="center" padding="8px" paddingLeft="0px" gap={8} alignSelf="stretch" marginRight="10px">
        <Text style={{ color: '#212B36', fontSize: 14, fontStyle: 'normal', fontWeight: 500, flex: '1 0 0' }}>Total Insured Value </Text>
        <Text
          maxWidth="100%"
          size="medium"
          color="#637381"
          maxLines={2}
          style={{ color: '#637381', fontSize: 14, fontStyle: 'normal', fontWeight: 500, flex: '1 0 0' }}
          bold
          ma
          value={formatMoney(quote.coverageTotal, { prefix: '$' })}
        ></Text>
        <Box height="24px" width="24px" alignItems="center" flexDirection="row" marginRight={4}></Box>
      </LayoutBox>
      <LayoutBox row alignItems="center" padding="8px" paddingLeft="0px" gap={8} alignSelf="stretch" marginRight="10px">
        <Text style={{ color: '#212B36', fontSize: 14, fontStyle: 'normal', fontWeight: 500, flex: '1 0 0' }}>Locations </Text>
        <Text
          maxWidth="100%"
          size="medium"
          color="#637381"
          maxLines={2}
          style={{ color: '#637381', fontSize: 14, fontStyle: 'normal', fontWeight: 500, flex: '1 0 0' }}
          bold
          ma
          value={`${locations.length}`}
        ></Text>
        <Box height="24px" width="24px" alignItems="center" flexDirection="row" marginRight={4}></Box>
      </LayoutBox>
      <LayoutBox row alignItems="center" padding="8px" paddingLeft="0px" gap={8} alignSelf="stretch" marginRight="10px">
        <Text style={{ color: '#212B36', fontSize: 14, fontStyle: 'normal', fontWeight: 500, flex: '1 0 0' }}>Adjustment Factor </Text>
        <Box style={{ color: '#637381', fontSize: 14, fontStyle: 'normal', fontWeight: 500, flex: '1 0 0' }}>
          {showPremium &&
            showAdjustmentFactor &&
            (startEditingAdjustment && adjusting ? (
              <EditableField
                component={
                  <Controller
                    name="quote.adjustmentFactor"
                    rules={{ required: 'Required' }}
                    render={({ field: { value, ref, onChange, onBlur }, fieldState: { error } }) => {
                      return (
                        <TextInput
                          value={value !== null ? value : ''}
                          variant="filled"
                          placeholder="0"
                          type="number"
                          error={error}
                          helperText={error ? error.message : null}
                          onChangeValue={(v) => {
                            onChange(v);
                            setAdjustmentFactorValue(Number(v).toFixed(6));
                          }}
                          onBlur={(e, valueError) => {
                            if (valueError && valueError.fixed) {
                              setValue('quote.adjustmentFactor', valueError.fixed, { shouldValidate: true });
                            } else if (value === null) {
                              setValue('quote.adjustmentFactor', null, { shouldValidate: true });
                            }
                            onBlur(e);
                          }}
                          inputRef={ref}
                        />
                      );
                    }}
                  />
                }
                shouldShowPicker={false}
              />
            ) : (
              <div
                style={{ display: 'flex', alignItems: 'center', cursor: `${adjusting ? 'pointer' : ''}` }}
                onMouseEnter={() => {
                  canAdjustQuote && handleSetFieldEditable('adjustmentFactor');
                }}
              >
                <Text maxWidth="100%" size="medium" color="#637381" maxLines={2} bold ma>
                  {adjustmentFactorValue}
                </Text>
              </div>
            ))}
        </Box>
        <Box height="24px" width="24px" alignItems="center" flexDirection="row" marginRight={4}>
          {isAdjustmentEditable && !startEditingAdjustment && adjusting ? (
            <Box
              onPress={() => {
                setStartEditingAdjustment(true);
              }}
            >
              <EditIcon />
            </Box>
          ) : (
            startEditingAdjustment &&
            adjusting && (
              <Box
                flexDirection="row"
                onPress={() => {
                  setStartEditingAdjustment(false);
                  adjusting && setValue('quote.adjustmentFactor', Number(adjustmentFactorValue));
                }}
              >
                <Box>
                  <GreenCheckIcon />
                </Box>
                <Box
                  onPress={() => {
                    setStartEditingAdjustment(false);
                    adjusting && setValue('quote.adjustmentFactor', adjustmentFactor.toFixed(6));
                    setAdjustmentFactorValue(adjustmentFactor.toFixed(6));
                  }}
                >
                  <CrossIcon />
                </Box>
              </Box>
            )
          )}
        </Box>
      </LayoutBox>
      <LayoutBox row alignItems="center" padding="8px" paddingLeft="0px" gap={8} alignSelf="stretch" marginRight="10px">
        <Text style={{ color: '#212B36', fontSize: 14, fontStyle: 'normal', fontWeight: 500, flex: '1 0 0' }}>Target Premium </Text>
        <Box style={{ color: '#637381', fontSize: 14, fontStyle: 'normal', fontWeight: 500, flex: '1 0 0' }}>
          {showPremium &&
            showAdjustmentFactor &&
            (startEditingPremium && adjusting ? (
              <EditableField
                component={
                  <Controller
                    name="quote.targetPremium"
                    render={({ field: { value, ref, onChange, onBlur }, fieldState: { error } }) => {
                      return (
                        <TextInput
                          label="Target Premium"
                          value={value}
                          variant="filled"
                          placeholder="0"
                          type="number"
                          error={error}
                          helperText={error ? error.message : null}
                          onChangeValue={(v) => {
                            onChange(v);
                            setTargetPremiumValue(v);
                          }}
                          onBlur={(e, valueError) => {
                            if (valueError && valueError.fixed) {
                              setValue('quote.targetPremium', valueError.fixed, { shouldValidate: true });
                            } else if (value === null) {
                              setValue('quote.targetPremium', null, { shouldValidate: true });
                            }
                            onBlur(e);
                          }}
                          inputRef={ref}
                          LabelProps={{ weight: '$bold', size: 'small' }}
                          hideHelperText={false}
                        />
                      );
                    }}
                  />
                }
                shouldShowPicker={false}
              />
            ) : (
              <div
                style={{ display: 'flex', alignItems: 'center', cursor: `${adjusting ? 'pointer' : ''}` }}
                onMouseEnter={() => {
                  canAdjustQuote && handleSetFieldEditable('targetPremium');
                }}
              >
                <Text maxWidth="100%" size="medium" color="#637381" maxLines={2} bold ma>
                  {targetPremiumValue || ' '}
                </Text>
              </div>
            ))}
        </Box>
        <Box height="24px" width="24px" alignItems="center" flexDirection="row" marginRight={4}>
          {isPremiumEditable && !startEditingPremium && adjusting ? (
            <Box
              onPress={() => {
                setStartEditingPremium(true);
              }}
            >
              <EditIcon />
            </Box>
          ) : (
            startEditingPremium &&
            adjusting && (
              <Box flexDirection="row">
                {/* Confirm belongs on the icon, not the row: with it on the wrapper, a press on the
                    clear button below also ran it, restoring the value being cleared. */}
                <Box
                  onPress={() => {
                    adjusting && targetPremiumValue && setValue('quote.targetPremium', Number(targetPremiumValue));
                    adjusting && setValue('quote.lossPick', null);
                    setLossPickValue('');
                    setStartEditingPremium(false);
                  }}
                >
                  <GreenCheckIcon />
                </Box>
                <Box
                  onPress={() => {
                    adjusting && setValue('quote.targetPremium', null);
                    setTargetPremiumValue(null);
                    setStartEditingPremium(false);
                  }}
                >
                  <CrossIcon />
                </Box>
              </Box>
            )
          )}
        </Box>
      </LayoutBox>
      <LayoutBox row alignItems="center" padding="8px" paddingLeft="0px" gap={8} alignSelf="stretch" marginRight="10px">
        <Text style={{ color: '#212B36', fontSize: 14, fontStyle: 'normal', fontWeight: 500, flex: '1 0 0' }}>Target Loss Pick (%) </Text>
        <Box style={{ color: '#637381', fontSize: 14, fontStyle: 'normal', fontWeight: 500, flex: '1 0 0' }}>
          {showPremium &&
            showAdjustmentFactor &&
            (startEditingLossPick && adjusting ? (
              <EditableField
                component={
                  <Controller
                    name="quote.lossPick"
                    render={({ field: { value, ref, onChange, onBlur }, fieldState: { error } }) => {
                      return (
                        <TextInput
                          label="Target Loss Pick (%)"
                          value={value}
                          variant="filled"
                          placeholder="0"
                          type="number"
                          error={error}
                          helperText={error ? error.message : null}
                          onChangeValue={(v) => {
                            onChange(v);
                            setLossPickValue(v);
                          }}
                          onBlur={(e, valueError) => {
                            if (valueError && valueError.fixed) {
                              setValue('quote.lossPick', valueError.fixed, { shouldValidate: true });
                            } else if (value === null) {
                              setValue('quote.lossPick', null, { shouldValidate: true });
                            }
                            onBlur(e);
                          }}
                          inputRef={ref}
                          LabelProps={{ weight: '$bold', size: 'small' }}
                          hideHelperText={false}
                        />
                      );
                    }}
                  />
                }
                shouldShowPicker={false}
              />
            ) : (
              <div
                style={{ display: 'flex', alignItems: 'center', cursor: `${adjusting ? 'pointer' : ''}` }}
                onMouseEnter={() => {
                  canAdjustQuote && handleSetFieldEditable('lossPick');
                }}
              >
                <Text maxWidth="100%" size="medium" color="#637381" maxLines={2} bold ma>
                  {lossPickValue || ' '}
                </Text>
              </div>
            ))}
        </Box>
        <Box height="24px" width="24px" alignItems="center" flexDirection="row" marginRight={4}>
          {isLossPickEditable && !startEditingLossPick && adjusting ? (
            <Box
              onPress={() => {
                setStartEditingLossPick(true);
              }}
            >
              <EditIcon />
            </Box>
          ) : (
            startEditingLossPick &&
            adjusting && (
              <Box flexDirection="row">
                {/* Confirm belongs on the icon, not the row: with it on the wrapper, a press on the
                    clear button below also ran it, restoring the pick and nulling the target premium. */}
                <Box
                  onPress={() => {
                    adjusting && lossPickValue && setValue('quote.lossPick', Number(lossPickValue));
                    adjusting && setValue('quote.targetPremium', null);
                    setTargetPremiumValue('');
                    setStartEditingLossPick(false);
                  }}
                >
                  <GreenCheckIcon />
                </Box>
                <Box
                  onPress={() => {
                    adjusting && setValue('quote.lossPick', null);
                    setLossPickValue('');
                    setStartEditingLossPick(false);
                  }}
                >
                  <CrossIcon />
                </Box>
              </Box>
            )
          )}
        </Box>
      </LayoutBox>
      <LayoutBox row alignItems="center" padding="8px" paddingLeft="0px" gap={8} alignSelf="stretch" marginRight="10px">
        <Text style={{ color: '#212B36', fontSize: 14, fontStyle: 'normal', fontWeight: 500, flex: '1 0 0' }}>
          Per Occurrence Deductible Macavity Runs{' '}
        </Text>
        <Box style={{ color: '#637381', fontSize: 14, fontStyle: 'normal', fontWeight: 500, flex: '1 0 0' }}>
          {showPremium &&
            showAdjustmentFactor &&
            (startEditingPerOccDed && adjusting ? (
              <EditableField
                component={
                  <Controller
                    name="quote.arrayOfPerOccDedOptions"
                    render={({ field: { value, ref, onChange, onBlur }, fieldState: { error } }) => {
                      return (
                        <TextInput
                          value={arrayOfPerOccDedOptionsValue}
                          variant="filled"
                          placeholder="0,100000,200000"
                          error={error}
                          helperText={error ? error.message : null}
                          onChangeValue={(v) => {
                            // Update local state for immediate display
                            setArrayOfPerOccDedOptionsValue(v || '');
                            // Convert to array for the form value
                            const arrayValue =
                              v && v.trim()
                                ? v
                                    .split(',')
                                    .map((item) => {
                                      const trimmed = item.trim();
                                      return trimmed && !isNaN(trimmed) ? Number(trimmed) : null;
                                    })
                                    .filter((item) => item !== null)
                                : [];
                            onChange(arrayValue);
                          }}
                          onBlur={(e) => {
                            onBlur(e);
                          }}
                          inputRef={ref}
                        />
                      );
                    }}
                  />
                }
                shouldShowPicker={false}
              />
            ) : (
              <div
                style={{ display: 'flex', alignItems: 'center', cursor: `${adjusting ? 'pointer' : ''}` }}
                onMouseEnter={() => {
                  canAdjustQuote && handleSetFieldEditable('arrayOfPerOccDedOptions');
                }}
              >
                <Text maxWidth="100%" size="medium" color="#637381" maxLines={2} bold ma>
                  {arrayOfPerOccDedOptionsValue || (quote.arrayOfPerOccDedOptions ? quote.arrayOfPerOccDedOptions.join(',') : ' ')}
                </Text>
              </div>
            ))}
        </Box>
        <Box height="24px" width="24px" alignItems="center" flexDirection="row" marginRight={4}>
          {isPerOccDedEditable && !startEditingPerOccDed && adjusting ? (
            <Box
              onPress={() => {
                setStartEditingPerOccDed(true);
              }}
            >
              <EditIcon />
            </Box>
          ) : (
            startEditingPerOccDed &&
            adjusting && (
              <Box
                flexDirection="row"
                onPress={() => {
                  adjusting &&
                    arrayOfPerOccDedOptionsValue &&
                    setValue(
                      'quote.arrayOfPerOccDedOptions',
                      arrayOfPerOccDedOptionsValue
                        .split(',')
                        .map((item) => {
                          const trimmed = item.trim();
                          return trimmed ? Number(trimmed) : null;
                        })
                        .filter((item) => item !== null)
                    );
                  setStartEditingPerOccDed(false);
                }}
              >
                <Box>
                  <GreenCheckIcon />
                </Box>
                <Box
                  onPress={() => {
                    adjusting && setValue('quote.arrayOfPerOccDedOptions', null);
                    setArrayOfPerOccDedOptionsValue('');
                    setStartEditingPerOccDed(false);
                  }}
                >
                  <CrossIcon />
                </Box>
              </Box>
            )
          )}
        </Box>
      </LayoutBox>
      <LayoutBox row alignItems="center" padding="8px" paddingLeft="0px" gap={8} alignSelf="stretch" marginRight="10px">
        <Box style={{ flex: '1 0 0' }}></Box>
        <Box style={{ flex: '1 0 0' }}>
          {adjusting && (
            <Button
              label="Keep Rate Consistent"
              variant="outlined"
              size="small"
              color="$primary"
              onPress={() => {
                const { quoteRate } = data;

                const formValues = getValues();
                const locations = formValues?.quote?.locations ?? quote.locations ?? [];
                const totalInsuredValue = locations.reduce((total, location) => {
                  return total + (Number(location?.coveredValue) || 0);
                }, 0);

                if (quoteRate && totalInsuredValue) {
                  // Get current form values for coverage dates (includes any user edits)
                  const formValues = getValues();
                  const currentCoverageAt = formValues.quote?.coverageAt || quote.coverageAt;
                  const currentCoverageUntil = formValues.quote?.coverageUntil || quote.coverageUntil;

                  // Calculate proportion of year based on coverage dates
                  const coverageAt = moment.utc(currentCoverageAt);
                  const coverageUntil = moment.utc(currentCoverageUntil);
                  const effectiveDays = coverageUntil.diff(coverageAt, 'days');

                  // Calculate proportion of year using actual days difference
                  const proportionOfYear = effectiveDays / 365;

                  // Calculate target premium with proportion of year
                  const calculatedTargetPremium = parseFloat(((totalInsuredValue * quoteRate * proportionOfYear) / 100).toFixed(2));
                  setValue('quote.targetPremium', calculatedTargetPremium);
                  setTargetPremiumValue(calculatedTargetPremium);
                }
              }}
            />
          )}
        </Box>
        <Box height="24px" width="24px" alignItems="center" flexDirection="row" marginRight={4}></Box>
      </LayoutBox>
    </LayoutBox>
  );
};

const CustomerDetailsSection = ({ quote }) => {
  const btnRef = useRef();
  const { generateQuoteDocs } = useApi();
  const navigate = useNavigate();
  const { application } = quote;
  // Straight from the context, the way OverViewSection above also reads `data` from it: the loss grid's
  // own reload after a save runs through this same `update`, so the Change control below reuses it
  // rather than threading another refresh prop down through NewQuoteDetails.
  const { update: refreshApplicationData } = useQuoteData();
  const [{ canViewWorkbook }] = useAppState();
  const [menuOpen, setMenuOpen] = useState(false);
  const [workbookModalOpen, setWorkbookModalOpen] = useState(false);
  const [apiError, setApiError] = useState(false);

  const [loading, setLoading] = useState(false);
  const [successFulApiCall, setSuccessFulApiCall] = useState(false);
  const [updatedUrls, setUpdatedUrls] = useState({});

  const getAgentName = (agent) => {
    if (typeof agent.name === 'string') return agent.name.split('@')[0];
    else if (agent.email) return agent.email.split('@')[0];
    else return 'Not listed';
  };

  const getStates = (locations) => {
    const regionCodes = locations.map((location) => location.regionCode);
    const uniqueSortedRegionCodes = [...new Set(regionCodes)].sort();
    return uniqueSortedRegionCodes.join(', ');
  };

  const customerDetails = [
    { label: 'Agent', value: getAgentName(quote.agent) },
    { label: 'Broker', value: quote.broker.name },
    { label: 'Applied At', value: moment.utc(application.appliedAt).format('MM-DD-YYYY') },
    { label: 'Target Date', value: moment.utc(application.targetEffectiveAt).format('MM-DD-YYYY') },
    { label: 'States', value: getStates(quote.locations) },
  ];

  const handleDownloadItem = useCallback(
    async (itemType) => {
      let url;
      switch (itemType) {
        case 'quote':
          if (updatedUrls.quoteDocumentUrl) {
            url = updatedUrls.quoteDocumentUrl;
          } else {
            url = quote.quoteDocumentUrl;
          }
          break;
        case 'draftPolicy':
          if (updatedUrls.draftPolicyUrl) {
            url = updatedUrls.draftPolicyUrl;
          } else {
            url = quote.draftPolicyUrl;
          }
          break;
        case 'binder':
          if (updatedUrls.binderUrl) {
            url = updatedUrls.binderUrl;
          } else {
            url = quote.binderUrl;
          }
          break;
        default:
          console.error('Invalid item type:', itemType);
          return;
      }
      if (!url) {
        console.error('No document URL available for item type:', itemType);
        return;
      }
      window.open(url, '_blank');
    },
    [quote, updatedUrls]
  );

  const { execute: downloadItem } = useAsync(handleDownloadItem, { immediate: false });

  const handleGenerateQuoteDocs = async (quoteId) => {
    setLoading(true);
    setSuccessFulApiCall(false);
    setApiError(false);

    try {
      const response = await generateQuoteDocs(quoteId);
      if (response && !response.errorMessage) {
        setUpdatedUrls({
          quoteDocumentUrl: response.quoteDocumentUrl,
          draftPolicyUrl: response.draftPolicyUrl,
          binderUrl: response.binderUrl,
        });
        setLoading(false);
        setSuccessFulApiCall(true);
      } else {
        setLoading(false);
        setApiError(true);
      }
    } catch (error) {
      setLoading(false);
      setApiError(true);
    }
  };

  const InfoRow = ({ label, value }) => (
    <LayoutBox row alignItems="center" padding="8px" paddingLeft="0px" gap={8} alignSelf="stretch">
      <Text style={{ color: '#212B36', fontSize: 14, fontStyle: 'normal', fontWeight: 500, flex: '1 0 0' }}>{label}</Text>
      <Text maxWidth="100%" bold style={{ color: '#637381', fontSize: 14, fontStyle: 'normal', fontWeight: 500, flex: '1 0 0' }}>
        {value}
      </Text>
    </LayoutBox>
  );

  return (
    <LayoutBox
      row
      flexDirection="column"
      alignItems="flex-start"
      alignSelf="stretch"
      flex="1 0 0"
      padding="24px"
      gap={4}
      borderRadius="8px"
      width="50%"
      backgroundColor="#fff"
    >
      <Box flexDirection="row" justifyContent="space-between" alignItems="center" width="100%">
        <Text row style={{ fontWeight: 600, fontSize: 16, maxLines: 2 }} color=" #212B36">
          {quote.insured}
        </Text>
        <Box justifyContent="flex-end" onPress={() => setMenuOpen(true)} ref={btnRef}>
          <Menu
            anchorNode={btnRef.current}
            open={menuOpen}
            onClose={() => setMenuOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            rounded={20}
          >
            <MenuItem
              px={'$1'}
              py={'3'}
              width="220px"
              onClick={() => {
                navigate.to(`/applications/${application.id}`);
              }}
            >
              <Text color="#000" bold>
                View Application
              </Text>
            </MenuItem>
            <MenuItem px={'$1'} py={'2'} onClick={() => handleGenerateQuoteDocs(quote.id)}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <Text color="#000" bold>
                  Refresh Document
                </Text>
                <span>
                  {loading && <ActivityIndicator color="#00ff00" size="small" />}
                  {successFulApiCall && <ShieldCheck />}
                  {apiError && <RedCrossIcon />}
                </span>
              </div>
            </MenuItem>
            {canViewWorkbook && (
              <>
                <Box px={'$1'} py={'2'} borderTopWidth={1} borderTopColor="$gray.200">
                  <Text color="#212B36">Download</Text>
                </Box>
                <MenuItem
                  px={'$1'}
                  py={'2'}
                  onClick={() => {
                    setMenuOpen(false);
                    setWorkbookModalOpen(true);
                  }}
                >
                  <Text color="#000" bold>
                    Workbook
                  </Text>
                </MenuItem>
                <MenuItem px={'$1'} py={'2'} onClick={() => downloadItem('quote')}>
                  <Text color="#000" bold>
                    Quote
                  </Text>
                </MenuItem>
                <MenuItem px={'$1'} py={'2'} onClick={() => downloadItem('draftPolicy')}>
                  <Text color="#000" bold>
                    Sample Policy
                  </Text>
                </MenuItem>
                <MenuItem px={'$1'} py={'2'} onClick={() => downloadItem('binder')}>
                  <Text color="#000" bold>
                    Binder
                  </Text>
                </MenuItem>
              </>
            )}
          </Menu>
          <EllipsisIcon />
        </Box>
      </Box>
      <Text row style={{ fontWeight: 400, fontSize: 14, maxLines: 2 }} color=" #212B36">
        {application?.customer && application.customer?.address}
      </Text>
      {/* Not InfoRow: that puts its `value` inside a Text, and CustomerAttachment renders a Box (with a
          Change button in it), not text. */}
      <LayoutBox row alignItems="center" padding="8px" paddingLeft="0px" gap={8} alignSelf="stretch">
        <Text style={{ color: '#212B36', fontSize: 14, fontStyle: 'normal', fontWeight: 500, flex: '1 0 0' }}>Customer</Text>
        <Box style={{ flex: '1 0 0' }}>
          <CustomerAttachment application={application} onChanged={() => refreshApplicationData(true)} />
        </Box>
      </LayoutBox>
      {customerDetails.map((item, index) => (
        <InfoRow key={index} label={item.label} value={item.value} />
      ))}
      {canViewWorkbook && <GenerateWorkbookModal quoteId={quote.id} open={workbookModalOpen} onClose={() => setWorkbookModalOpen(false)} />}
    </LayoutBox>
  );
};

// Reusable editable field component following React best practices
// Uses ref to track editing state to prevent hover interference
const PolicyEditableField = ({
  fieldName,
  initialValue,
  isEditable,
  onSetEditable,
  adjusting,
  canAdjust,
  setValue,
  setLocalValue,
  fieldType = 'single',
  placeholder = '',
  editingFieldRef,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [displayValue, setDisplayValue] = useState('');

  useEffect(() => {
    if (fieldType === 'array') {
      setDisplayValue(typeof initialValue === 'string' ? initialValue : '');
    } else if (fieldType === 'money') {
      setDisplayValue(initialValue ? initialValue.toString() : '');
    } else {
      setDisplayValue(initialValue ? initialValue.toString() : '');
    }
  }, [initialValue, fieldType]);

  const handleSave = () => {
    if (adjusting) {
      let valueToSave;

      if (fieldType === 'array') {
        valueToSave = displayValue
          .split(',')
          .map((item) => item.trim())
          .filter((item) => item.length > 0);
        setLocalValue(displayValue);
      } else if (fieldType === 'money') {
        valueToSave = displayValue ? Number(displayValue) : null;
        setLocalValue(displayValue);
      } else {
        valueToSave = displayValue;
        setLocalValue(displayValue);
      }

      setValue(fieldName, valueToSave);
    }
    setIsEditing(false);
    if (editingFieldRef) {
      editingFieldRef.current = null;
    }
  };

  const handleCancel = () => {
    if (fieldType === 'array') {
      setDisplayValue(typeof initialValue === 'string' ? initialValue : '');
    } else {
      setDisplayValue(initialValue ? initialValue.toString() : '');
    }
    setIsEditing(false);
    if (editingFieldRef) {
      editingFieldRef.current = null;
    }
  };

  const handleStartEditing = () => {
    setIsEditing(true);
    if (editingFieldRef) {
      editingFieldRef.current = fieldName;
    }
  };

  const handleMouseEnter = () => {
    // Only allow hover to set editable if no field is currently being edited
    if (canAdjust && (!editingFieldRef || !editingFieldRef.current)) {
      onSetEditable();
    }
  };

  const renderDisplayValue = () => {
    if (fieldType === 'array') {
      const arrayValue = displayValue
        ? displayValue
            .split(',')
            .map((s) => s.trim())
            .filter((s) => s)
        : [];
      if (arrayValue.length === 0) {
        return <Text style={{ color: '#637381', fontSize: 14, fontStyle: 'italic' }}>Not specified</Text>;
      }
      return arrayValue.map((item, index) => (
        <Text key={index} style={{ color: '#637381', fontSize: 14 }}>
          {item}
        </Text>
      ));
    } else if (fieldType === 'money') {
      if (!displayValue) {
        return <Text style={{ color: '#637381', fontSize: 14, fontStyle: 'italic' }}>Not specified</Text>;
      }
      const numValue = Number(displayValue) || 0;
      return <Text style={{ color: '#637381', fontSize: 14 }}>{formatMoney(numValue, { prefix: '$' })}</Text>;
    } else {
      return <Text style={{ color: '#637381', fontSize: 14 }}>{displayValue || 'Not specified'}</Text>;
    }
  };

  if (isEditing && adjusting) {
    return (
      <Box style={{ flex: '1 0 0', display: 'flex', alignItems: 'center', gap: '8px', flexDirection: 'row' }}>
        <input
          value={displayValue}
          onChange={(e) => setDisplayValue(e.target.value)}
          placeholder={placeholder}
          type={fieldType === 'money' ? 'number' : 'text'}
          style={{
            flex: 1,
            padding: '8px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            fontSize: '14px',
          }}
        />
        <Box flexDirection="row" alignItems="center" gap={4}>
          <Box onPress={handleSave}>
            <GreenCheckIcon />
          </Box>
          <Box onPress={handleCancel}>
            <CrossIcon />
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box style={{ flex: '1 0 0', display: 'flex', alignItems: 'center', gap: '8px', flexDirection: 'row' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          flexDirection: 'column',
          cursor: adjusting ? 'pointer' : '',
          flex: 1,
        }}
        onMouseEnter={handleMouseEnter}
      >
        {renderDisplayValue()}
      </div>
      {isEditable && !isEditing && adjusting && (
        <Box onPress={handleStartEditing}>
          <EditIcon />
        </Box>
      )}
    </Box>
  );
};

const PolicyDetails = ({ quote }) => {
  const { adjusting } = useAdjustmentState();
  const { setValue } = useAdjustmentForm();
  const [{ canAdjustQuote }] = useAppState();

  // Use ref to track which field is currently being edited (best practice: refs don't trigger re-renders)
  const editingFieldRef = useRef(null);
  const [currentEditableField, setCurrentEditableField] = useState(null);

  const [maxLimitValue, setMaxLimitValue] = useState(quote.perCarOccurrenceLimit?.toString() || '250000');
  const [namedInsuredsValue, setNamedInsuredsValue] = useState(Array.isArray(quote.namedInsureds) ? quote.namedInsureds.join(', ') : '');
  const [perOccurrenceDeductibleValue, setPerOccurrenceDeductibleValue] = useState(quote.perOccurrenceDeductible?.toString() || '');

  useEffect(() => {
    setMaxLimitValue(quote.perCarOccurrenceLimit?.toString() || '250000');
    setNamedInsuredsValue(Array.isArray(quote.namedInsureds) ? quote.namedInsureds.join(', ') : '');
    setPerOccurrenceDeductibleValue(quote.perOccurrenceDeductible?.toString() || '');
  }, [quote]);

  const handleSetFieldEditable = (fieldName) => {
    // Only allow changing editable field if no field is currently being edited
    if (!editingFieldRef.current) {
      setCurrentEditableField(fieldName);
    }
  };

  const InfoRow = ({ label, value }) => (
    <LayoutBox row alignItems="flex-start" padding="8px" paddingLeft="0px" gap={8} alignSelf="stretch">
      <Text style={{ color: '#212B36', fontSize: 14, fontStyle: 'normal', fontWeight: 500, flex: '1 0 0' }}>{label}</Text>
      <Box style={{ color: '#637381', fontSize: 14, fontStyle: 'normal', fontWeight: 500, flex: '1 0 0' }}>
        {Array.isArray(value) ? (
          value.map((item, index) => (
            <Text key={index} maxWidth="100%" style={{ color: '#637381', fontSize: 14, fontStyle: 'normal', fontWeight: 500 }}>
              {item}
            </Text>
          ))
        ) : (
          <Text maxWidth="100%" style={{ color: '#637381', fontSize: 14, fontStyle: 'normal', fontWeight: 500 }}>
            {value}
          </Text>
        )}
      </Box>
    </LayoutBox>
  );

  const InfoEditableRow = ({ label, fieldName, fieldType, localValue, setLocalValue, placeholder = '' }) => (
    <LayoutBox row alignItems="flex-start" padding="8px" paddingLeft="0px" gap={8} alignSelf="stretch">
      <Text style={{ color: '#212B36', fontSize: 14, fontStyle: 'normal', fontWeight: 500, flex: '1 0 0' }}>{label}</Text>
      <PolicyEditableField
        fieldName={fieldName}
        initialValue={fieldType === 'money' ? Number(localValue) : localValue}
        isEditable={currentEditableField === fieldName}
        onSetEditable={() => handleSetFieldEditable(fieldName)}
        adjusting={adjusting}
        canAdjust={canAdjustQuote}
        setValue={setValue}
        setLocalValue={setLocalValue}
        fieldType={fieldType}
        placeholder={placeholder}
        editingFieldRef={editingFieldRef}
      />
    </LayoutBox>
  );

  const maxLimitPerCoveredAuto = quote.perCarOccurrenceLimit
    ? formatMoney(quote.perCarOccurrenceLimit, { prefix: '$' })
    : `Default (${formatMoney(250000, { prefix: '$' })})`;
  const namedInsureds = quote.namedInsureds || ['Not specified'];
  const perOccurrenceDeductibleDisplay = quote.perOccurrenceDeductible
    ? formatMoney(quote.perOccurrenceDeductible, { prefix: '$' })
    : 'Not specified';

  return (
    <LayoutBox
      row
      flexDirection="column"
      alignItems="flex-start"
      alignSelf="stretch"
      flex="1 0 0"
      padding="24px"
      gap={4}
      borderRadius="8px"
      width="50%"
      backgroundColor="#fff"
    >
      <Text row style={{ fontWeight: 600, fontSize: 16, maxLines: 2 }} color="#212B36">
        Understory Dealers Open Lot - Policy Details
      </Text>

      {/* Maximum Limit Per Covered Auto - always visible */}
      {adjusting ? (
        <InfoEditableRow
          label="Maximum Limit Per Covered Auto"
          fieldName="quote.perCarOccurrenceLimit"
          fieldType="money"
          localValue={maxLimitValue}
          setLocalValue={setMaxLimitValue}
          placeholder="Enter amount (numbers only)"
        />
      ) : (
        <InfoRow label="Maximum Limit Per Covered Auto" value={maxLimitPerCoveredAuto} />
      )}

      {adjusting ? (
        <>
          <InfoEditableRow
            label="Named Insureds"
            fieldName="quote.namedInsureds"
            fieldType="array"
            localValue={namedInsuredsValue}
            setLocalValue={setNamedInsuredsValue}
            placeholder="Enter names separated by commas"
          />
          <InfoEditableRow
            label="Per Occurrence Deductible"
            fieldName="quote.perOccurrenceDeductible"
            fieldType="money"
            localValue={perOccurrenceDeductibleValue}
            setLocalValue={setPerOccurrenceDeductibleValue}
            placeholder="Enter deductible amount"
          />
        </>
      ) : (
        <>
          <InfoRow label="Named Insureds" value={namedInsureds} />
          <InfoRow label="Per Occurrence Deductible" value={perOccurrenceDeductibleDisplay} />
        </>
      )}
    </LayoutBox>
  );
};

const FalsePretense = ({ quote }) => {
  const { adjusting } = useAdjustmentState();
  const { setValue } = useAdjustmentForm();
  const [{ canAdjustQuote }] = useAppState();

  // Use ref to track which field is currently being edited (best practice: refs don't trigger re-renders)
  const editingFieldRef = useRef(null);
  const [currentEditableField, setCurrentEditableField] = useState(null);

  const falsePretenseCoverage = quote.locations[0]?.coverages?.find((cov) => cov.coverageType === 'falsePretense');
  const falsePretenseCoverageIndex = quote.locations[0]?.coverages?.findIndex((cov) => cov.coverageType === 'falsePretense');

  const [perAutoDeductibleValue, setPerAutoDeductibleValue] = useState(falsePretenseCoverage?.deductible?.toString() || '');
  const [limitPerLocationValue, setLimitPerLocationValue] = useState(quote.falsePretense?.falsePretensePerLocPerOcc?.toString() || '');
  const [limitOfInsuranceValue, setLimitOfInsuranceValue] = useState(quote.falsePretense?.falsePretensePolicyLimit?.toString() || '');
  const [perLossDeductibleValue, setPerLossDeductibleValue] = useState(quote.falsePretense?.falsePretensePerLossDeductible?.toString() || '');

  useEffect(() => {
    const coverage = quote.locations[0]?.coverages?.find((cov) => cov.coverageType === 'falsePretense');
    setPerAutoDeductibleValue(coverage?.deductible?.toString() || '');
    setLimitPerLocationValue(quote.falsePretense?.falsePretensePerLocPerOcc?.toString() || '');
    setLimitOfInsuranceValue(quote.falsePretense?.falsePretensePolicyLimit?.toString() || '');
    setPerLossDeductibleValue(quote.falsePretense?.falsePretensePerLossDeductible?.toString() || '');
  }, [quote]);

  const handleSetFieldEditable = (fieldName) => {
    // Only allow changing editable field if no field is currently being edited
    if (!editingFieldRef.current) {
      setCurrentEditableField(fieldName);
    }
  };

  const InfoRow = ({ label, value }) => (
    <LayoutBox row alignItems="center" padding="8px" paddingLeft="0px" gap={8} alignSelf="stretch">
      <Text style={{ color: '#212B36', fontSize: 14, fontStyle: 'normal', fontWeight: 500, flex: '1 0 0' }}>{label}</Text>
      <Text maxWidth="100%" bold style={{ color: '#637381', fontSize: 14, fontStyle: 'normal', fontWeight: 500, flex: '1 0 0' }}>
        {value}
      </Text>
    </LayoutBox>
  );

  const InfoEditableRow = ({
    label,
    fieldName,
    localValue,
    setLocalValue,
    defaultValue = null,
    showDefault = false,
    placeholder = 'Enter amount',
  }) => (
    <LayoutBox row alignItems="center" padding="8px" paddingLeft="0px" gap={8} alignSelf="stretch">
      <Text style={{ color: '#212B36', fontSize: 14, fontWeight: 500, flex: '1 0 0' }}>{label}</Text>
      <PolicyEditableField
        fieldName={fieldName}
        initialValue={Number(localValue) || defaultValue || 0}
        isEditable={currentEditableField === fieldName}
        onSetEditable={() => handleSetFieldEditable(fieldName)}
        adjusting={adjusting}
        canAdjust={canAdjustQuote}
        setValue={setValue}
        setLocalValue={setLocalValue}
        fieldType="money"
        placeholder={placeholder}
        editingFieldRef={editingFieldRef}
      />
    </LayoutBox>
  );

  const perAutoDeductible = falsePretenseCoverage?.deductible || null;
  const limitPerLocationPerOccurrence = quote.falsePretense?.falsePretensePerLocPerOcc || null;
  const limitOfInsurance = quote.falsePretense?.falsePretensePolicyLimit || null;
  const perLossDeductible = quote.falsePretense?.falsePretensePerLossDeductible || null;

  return (
    <LayoutBox
      row
      flexDirection="column"
      alignItems="flex-start"
      alignSelf="stretch"
      flex="1 0 0"
      padding="24px"
      gap={4}
      borderRadius="8px"
      width="50%"
      backgroundColor="#fff"
    >
      <Text row style={{ fontWeight: 600, fontSize: 16, maxLines: 2 }} color="#212B36">
        Understory Dealers Open Lot - False Pretense
      </Text>

      {adjusting ? (
        <>
          <InfoEditableRow
            label="Per Auto Deductible"
            fieldName={`quote.locations.0.coverages.${falsePretenseCoverageIndex}.deductible`}
            localValue={perAutoDeductibleValue}
            setLocalValue={setPerAutoDeductibleValue}
            placeholder="Enter deductible amount"
          />
          <InfoEditableRow
            label="Per Loss Deductible"
            fieldName="quote.falsePretense.falsePretensePerLossDeductible"
            localValue={perLossDeductibleValue}
            setLocalValue={setPerLossDeductibleValue}
            placeholder="Enter per loss deductible"
          />
          <InfoEditableRow
            label="Limit of Insurance Per Location Per Occurrence"
            fieldName="quote.falsePretense.falsePretensePerLocPerOcc"
            localValue={limitPerLocationValue}
            setLocalValue={setLimitPerLocationValue}
            defaultValue={100000}
            showDefault={true}
            placeholder="Enter limit per location"
          />
          <InfoEditableRow
            label="Limit of Insurance"
            fieldName="quote.falsePretense.falsePretensePolicyLimit"
            localValue={limitOfInsuranceValue}
            setLocalValue={setLimitOfInsuranceValue}
            defaultValue={200000}
            showDefault={true}
            placeholder="Enter insurance limit"
          />
        </>
      ) : (
        <>
          <InfoRow label="Per Auto Deductible" value={formatMoney(perAutoDeductible, { prefix: '$' })} />
          <InfoRow
            label="Per Loss Deductible"
            value={perLossDeductible == null ? 'Not Applicable' : formatMoney(perLossDeductible, { prefix: '$' })}
          />
          <InfoRow
            label="Limit of Insurance Per Location Per Occurrence"
            value={
              limitPerLocationPerOccurrence == null
                ? `Default (${formatMoney(100000, { prefix: '$' })})`
                : formatMoney(limitPerLocationPerOccurrence, { prefix: '$' })
            }
          />
          <InfoRow
            label="Limit of Insurance"
            value={limitOfInsurance == null ? `Default (${formatMoney(200000, { prefix: '$' })})` : formatMoney(limitOfInsurance, { prefix: '$' })}
          />
        </>
      )}
    </LayoutBox>
  );
};
// Parses "Name | Address, City State Zip" strings into {name, address, cityStateZip} objects
const parseLossPayeeString = (str) => {
  if (!str) return { name: '', address: '', cityStateZip: '' };
  const parts = str.split('|').map((s) => s.trim());
  const name = parts[0] || '';
  const fullAddress = parts.slice(1).join('|').trim();
  // Try to split address from city/state/zip by the last comma before a state+zip pattern
  const commaIdx = fullAddress.lastIndexOf(',');
  if (commaIdx > 0) {
    return { name, address: fullAddress.substring(0, commaIdx).trim(), cityStateZip: fullAddress.substring(commaIdx + 1).trim() };
  }
  return { name, address: fullAddress, cityStateZip: '' };
};

// Formats {name, address, cityStateZip} back to "Name | Address, City State Zip" for the API
const formatLossPayeeString = (row) => {
  const name = (row.name || '').trim();
  const address = (row.address || '').trim();
  const cityStateZip = (row.cityStateZip || '').trim();
  const fullAddress = [address, cityStateZip].filter(Boolean).join(', ');
  if (!name && !fullAddress) return '';
  if (!fullAddress) return name;
  return `${name} | ${fullAddress}`;
};

const LossPayeeSection = ({ quote }) => {
  const { adjusting } = useAdjustmentState();
  const { setValue } = useAdjustmentForm();
  const [rows, setRows] = useState(() => {
    const lp = quote.lossPayee;
    if (Array.isArray(lp) && lp.length > 0) return lp.map((s) => parseLossPayeeString(s));
    return [];
  });

  useEffect(() => {
    const lp = quote.lossPayee;
    if (Array.isArray(lp) && lp.length > 0) {
      setRows(lp.map((s) => parseLossPayeeString(s)));
    } else {
      setRows([]);
    }
  }, [quote.lossPayee]);

  const syncToForm = (updated) => {
    const apiValue = updated.map(formatLossPayeeString).filter((s) => s.length > 0);
    setValue('quote.lossPayee', apiValue.length > 0 ? apiValue : null);
  };

  const lossPayeeColumns = [
    { headerName: 'Loss Payee', field: 'name', editable: true },
    { headerName: 'Address', field: 'address', editable: true },
    { headerName: 'City, State Zip', field: 'cityStateZip', editable: true },
  ];

  const handleCellValueChanged = (params) => {
    const updated = rows.map((row, i) => (i === params.rowIndex ? { ...row, [params.colDef.field]: params.newValue } : row));
    setRows(updated);
    syncToForm(updated);
  };

  const handleAddRow = () => {
    const updated = [...rows, { name: '', address: '', cityStateZip: '' }];
    setRows(updated);
    syncToForm(updated);
  };

  const handleRemoveRow = () => {
    if (rows.length > 0) {
      const updated = rows.slice(0, -1);
      setRows(updated);
      syncToForm(updated);
    }
  };

  const entries = (quote.lossPayee || []).map((s) => parseLossPayeeString(s));

  return (
    <LayoutBox
      row
      flexDirection="column"
      alignItems="flex-start"
      alignSelf="stretch"
      flex="1 0 0"
      padding="24px"
      gap={4}
      borderRadius="8px"
      width="100%"
      backgroundColor="#fff"
    >
      <Text row style={{ fontWeight: 600, fontSize: 16, maxLines: 2 }} color="#212B36">
        Loss Payee
      </Text>

      {adjusting ? (
        <>
          <div className="grid-container" style={{ width: '100%', maxHeight: '400px', overflowY: 'auto' }}>
            <Grid
              data={rows}
              columnDefs={lossPayeeColumns}
              defaultColDef={{ flex: 1, resizable: true }}
              setGridReference={() => {}}
              floatingFilter={false}
              shouldShowPagination={false}
              onCellValueChanged={handleCellValueChanged}
            />
          </div>
          <LayoutBox row gap={8} marginTop="8px">
            <Button label="Add Row" variant="outlined" size="small" color="$primary" onPress={handleAddRow} />
            {rows.length > 0 && <Button label="Remove Row" variant="outlined" size="small" color="$primary" onPress={handleRemoveRow} />}
          </LayoutBox>
        </>
      ) : (
        <>
          {entries.length === 0 ? (
            <Text style={{ color: '#637381', fontSize: 14, fontStyle: 'italic' }}>None</Text>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '8px' }}>
              <thead>
                <tr>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '6px 8px',
                      fontSize: 13,
                      fontWeight: 500,
                      color: '#212B36',
                      borderBottom: '1px solid #E0E0E0',
                    }}
                  >
                    Loss Payee
                  </th>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '6px 8px',
                      fontSize: 13,
                      fontWeight: 500,
                      color: '#212B36',
                      borderBottom: '1px solid #E0E0E0',
                    }}
                  >
                    Address
                  </th>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '6px 8px',
                      fontSize: 13,
                      fontWeight: 500,
                      color: '#212B36',
                      borderBottom: '1px solid #E0E0E0',
                    }}
                  >
                    City, State Zip
                  </th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry, index) => (
                  <tr key={index}>
                    <td style={{ padding: '6px 8px', fontSize: 14, color: '#637381' }}>{entry.name || ''}</td>
                    <td style={{ padding: '6px 8px', fontSize: 14, color: '#637381' }}>{entry.address || ''}</td>
                    <td style={{ padding: '6px 8px', fontSize: 14, color: '#637381' }}>{entry.cityStateZip || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </LayoutBox>
  );
};

const LenderLossPayeeSection = ({ quote }) => {
  const { adjusting } = useAdjustmentState();
  const { setValue } = useAdjustmentForm();
  const [llpRows, setLlpRows] = useState(quote.lenderLossPayee || []);

  useEffect(() => {
    setLlpRows(quote.lenderLossPayee || []);
  }, [quote.lenderLossPayee]);

  const syncToForm = (updated) => {
    const filtered = updated.filter(
      (row) =>
        (row.lienHolder || '').trim() ||
        (row.lienHolderAddress || '').trim() ||
        (row.lienHolderCityStateZip || '').trim() ||
        (row.lienHolderLocations || '').trim()
    );
    setValue('quote.lenderLossPayee', filtered.length > 0 ? filtered : null);
  };

  const llpColumns = [
    { headerName: 'Lien Holder', field: 'lienHolder', editable: true },
    { headerName: 'Address', field: 'lienHolderAddress', editable: true },
    { headerName: 'City, State Zip', field: 'lienHolderCityStateZip', editable: true },
    { headerName: 'Locations', field: 'lienHolderLocations', editable: true },
  ];

  const handleCellValueChanged = (params) => {
    const updated = llpRows.map((row, i) => (i === params.rowIndex ? { ...row, [params.colDef.field]: params.newValue } : row));
    setLlpRows(updated);
    syncToForm(updated);
  };

  const handleAddRow = () => {
    const updated = [...llpRows, { lienHolder: '', lienHolderAddress: '', lienHolderCityStateZip: '', lienHolderLocations: '' }];
    setLlpRows(updated);
    syncToForm(updated);
  };

  const handleRemoveRow = () => {
    if (llpRows.length > 0) {
      const updated = llpRows.slice(0, -1);
      setLlpRows(updated);
      syncToForm(updated);
    }
  };

  const entries = quote.lenderLossPayee || [];

  return (
    <LayoutBox
      row
      flexDirection="column"
      alignItems="flex-start"
      alignSelf="stretch"
      flex="1 0 0"
      padding="24px"
      gap={4}
      borderRadius="8px"
      width="100%"
      backgroundColor="#fff"
    >
      <Text row style={{ fontWeight: 600, fontSize: 16, maxLines: 2 }} color="#212B36">
        Lender Loss Payee
      </Text>

      {adjusting ? (
        <>
          <div className="grid-container" style={{ width: '100%', maxHeight: '400px', overflowY: 'auto' }}>
            <Grid
              data={llpRows}
              columnDefs={llpColumns}
              defaultColDef={{ flex: 1, resizable: true }}
              setGridReference={() => {}}
              floatingFilter={false}
              shouldShowPagination={false}
              onCellValueChanged={handleCellValueChanged}
            />
          </div>
          <LayoutBox row gap={8} marginTop="8px">
            <Button label="Add Row" variant="outlined" size="small" color="$primary" onPress={handleAddRow} />
            {llpRows.length > 0 && <Button label="Remove Row" variant="outlined" size="small" color="$primary" onPress={handleRemoveRow} />}
          </LayoutBox>
        </>
      ) : (
        <>
          {entries.length === 0 ? (
            <Text style={{ color: '#637381', fontSize: 14, fontStyle: 'italic' }}>None</Text>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '8px' }}>
              <thead>
                <tr>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '6px 8px',
                      fontSize: 13,
                      fontWeight: 500,
                      color: '#212B36',
                      borderBottom: '1px solid #E0E0E0',
                    }}
                  >
                    Lien Holder
                  </th>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '6px 8px',
                      fontSize: 13,
                      fontWeight: 500,
                      color: '#212B36',
                      borderBottom: '1px solid #E0E0E0',
                    }}
                  >
                    Address
                  </th>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '6px 8px',
                      fontSize: 13,
                      fontWeight: 500,
                      color: '#212B36',
                      borderBottom: '1px solid #E0E0E0',
                    }}
                  >
                    City, State Zip
                  </th>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '6px 8px',
                      fontSize: 13,
                      fontWeight: 500,
                      color: '#212B36',
                      borderBottom: '1px solid #E0E0E0',
                    }}
                  >
                    Locations
                  </th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry, index) => (
                  <tr key={index}>
                    <td style={{ padding: '6px 8px', fontSize: 14, color: '#637381' }}>{entry.lienHolder || ''}</td>
                    <td style={{ padding: '6px 8px', fontSize: 14, color: '#637381' }}>{entry.lienHolderAddress || ''}</td>
                    <td style={{ padding: '6px 8px', fontSize: 14, color: '#637381' }}>{entry.lienHolderCityStateZip || ''}</td>
                    <td style={{ padding: '6px 8px', fontSize: 14, color: '#637381' }}>{entry.lienHolderLocations || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </LayoutBox>
  );
};

/* The carrier's policy number, for a product whose carrier issues them: saved on its own, then the
   quote is refreshed. */
const QuotePolicyNumberSection = ({ quote }) => {
  const [{ canAdjustQuote }] = useAppState();
  const { setQuotePolicyNumber } = useApi();
  const { update: refreshQuoteData } = useQuoteData();
  return (
    <QuotePolicyNumberField
      quote={quote}
      canEdit={Boolean(canAdjustQuote)}
      onSave={(policyNumber) => setQuotePolicyNumber(quote.id, policyNumber)}
      onSaved={() => refreshQuoteData()}
    />
  );
};

export const NewQuoteDetails = React.forwardRef((props, ref) => {
  const { quote } = props;
  const { data, totalAllInCost } = useQuoteData();
  const { showOverViewSection, setShowOverViewSection } = useAdjustmentState();

  const { netPremiumTotal, quoteRate } = data;
  const totalInsuredValue = quote.locations?.reduce((total, location) => {
    return total + (location.coveredValue || 0);
  }, 0);

  const quoteData = [
    {
      title: 'Gross Written Premium',
      text: `${formatMoney(netPremiumTotal, { prefix: '$', withDecimals: true })}`,
    },
    {
      title: 'Total All-In-Cost',
      text: `${formatMoney(totalAllInCost, { prefix: '$', withDecimals: true })}`,
    },
    {
      title: 'Rate',
      text: quoteRate ? `$ ${quoteRate} ` : '',
    },
    {
      title: '∑ Avg Total Insured Value',
      text: `${formatMoney(totalInsuredValue, { prefix: '$', withDecimals: true })}`,
      style: 'dot',
    },
  ];
  return (
    <Box ref={ref}>
      <LayoutBox gap={18}>
        <QuoteStat data={quoteData} />
        <QuotePolicyNumberSection quote={quote} />
      </LayoutBox>
      <div className={`details-container ${showOverViewSection ? 'open' : 'closed'}`} style={{ overflowY: 'auto' }}>
        <LayoutBox gap={18} marginBottom={15}>
          <LayoutBox row alignSelf="stretch" gap={32} alignItems="center" width="100%">
            <OverViewSection quote={quote} />
            <CustomerDetailsSection quote={quote} />
          </LayoutBox>
          <LayoutBox row alignSelf="stretch" gap={32} alignItems="center" width="100%">
            <PolicyDetails quote={quote} />
            <FalsePretense quote={quote} />
          </LayoutBox>
          <LayoutBox row alignSelf="stretch" gap={32} alignItems="flex-start" width="100%">
            <LossPayeeSection quote={quote} />
            <LenderLossPayeeSection quote={quote} />
          </LayoutBox>
        </LayoutBox>
      </div>
      <Box alignItems="start" bold>
        <Button
          color="$primary.light"
          variant="text"
          label={showOverViewSection ? 'Show Less' : 'Show More'}
          onPress={() => setShowOverViewSection(!showOverViewSection)}
        />
      </Box>
    </Box>
  );
});
